import HomeNavBar from "@/components/HomeNavBar/HomeNavBar.vue";
import { get_token } from "@/router";
import {
    build_credit_stats,
    build_customer_accounts,
    build_month_options,
    build_sales_stats,
    credit_order_matches,
    format_month_label,
    format_price,
    format_short_date,
    format_status_label,
    get_customer_initials,
    get_order_delivery_label,
    get_order_month_key,
    get_order_payment_label,
    get_order_products_label,
    get_order_status,
    is_credit_dashboard_order,
    is_credit_order,
    is_order_paid,
    sales_order_matches,
} from "./credit_management_helpers";

export default {
    name: "CreditManagementView",
    components: {
        HomeNavBar,
    },
    data() {
        return ({
            base_profile_id: this.$route.query.base_profile_id,
            loading: false,
            all_orders: [],
            active_dashboard_tab: "sales",
            sales_selected_month: "all",
            credit_selected_month: "all",
            customer_query: "",
            sales_query: "",
            sales_payment_filter: "all",
            sales_delivery_filter: "all",
            credit_status_filter: "all",
            sales_mobile_filter_by: "month",
            credit_mobile_filter_by: "month",
            selected_customer_id: null,
            customer_page: 1,
            customer_page_size: 10,
            sales_page: 1,
            sales_page_size: 10,
            expanded_sales_order_ids: [],
            expanded_customer_account_ids: [],
            is_loading: false,
            errors: {},
        });
    },
    computed: {
        month_filtered_orders() {
            return (this.all_orders || []).filter((order) => {
                return this.sales_selected_month === "all"
                    || get_order_month_key(order) === this.sales_selected_month;
            });
        },
        all_credit_orders() {
            return (this.all_orders || []).filter((order) => {
                return is_credit_dashboard_order(order);
            });
        },
        credit_orders() {
            return this.all_credit_orders.filter((order) => {
                return this.credit_selected_month === "all"
                    || get_order_month_key(order) === this.credit_selected_month;
            });
        },
        sales_orders() {
            return this.month_filtered_orders.filter((order) => {
                const status = get_order_status(order);
                return !["declined","rejected","pending", "cancelled"].includes(status);
            });
        },
        month_options() {
            return build_month_options(this.all_orders);
        },
        filtered_credit_orders() {
            const query = this.customer_query.trim().toLowerCase();
            const source_orders = this.selected_customer
                ? (this.selected_customer.orders || [])
                : this.all_credit_orders;

            return source_orders.filter((order) =>
                credit_order_matches(order, query, this.credit_selected_month, this.credit_status_filter)
            );
        },
        filtered_sales_orders() {
            const query = this.sales_query.trim().toLowerCase();

            return this.sales_orders.filter((order) =>
                sales_order_matches(order, query, this.sales_payment_filter, this.sales_delivery_filter)
            );
        },
        sales_total_pages() {
            return Math.max(1, Math.ceil(this.filtered_sales_orders.length / this.sales_page_size));
        },
        sales_page_start() {
            if (!this.filtered_sales_orders.length) return 0;
            return ((this.sales_page - 1) * this.sales_page_size) + 1;
        },
        sales_page_end() {
            if (!this.filtered_sales_orders.length) return 0;
            return Math.min(this.sales_page * this.sales_page_size, this.filtered_sales_orders.length);
        },
        paginated_sales_orders() {
            const start = (this.sales_page - 1) * this.sales_page_size;
            const end = start + this.sales_page_size;
            return this.filtered_sales_orders.slice(start, end);
        },
        sales_stats() {
            return build_sales_stats(this.sales_orders, this.sales_orders);
        },
        all_customer_accounts() {
            return build_customer_accounts(this.all_credit_orders);
        },
        customer_accounts() {
            return build_customer_accounts(this.filtered_credit_orders);
        },
        paginated_customer_accounts() {
            const start = (this.customer_page - 1) * this.customer_page_size;
            const end = start + this.customer_page_size;
            return this.customer_accounts.slice(start, end);
        },
        customer_total_pages() {
            return Math.max(1, Math.ceil(this.customer_accounts.length / this.customer_page_size));
        },
        customer_page_start() {
            if (!this.customer_accounts.length) return 0;
            return ((this.customer_page - 1) * this.customer_page_size) + 1;
        },
        customer_page_end() {
            if (!this.customer_accounts.length) return 0;
            return Math.min(this.customer_page * this.customer_page_size, this.customer_accounts.length);
        },
        credit_stats() {
            return build_credit_stats(this.filtered_credit_orders, this.customer_accounts);
        },
        selected_customer() {
            if (!this.all_customer_accounts.length || !this.selected_customer_id) return null;
            return this.all_customer_accounts.find((customer) => customer.id === this.selected_customer_id) || null;
        },
        selected_customer_orders() {
            return this.selected_customer ? this.filtered_credit_orders : [];
        },
        selected_customer_unpaid_orders() {
            return this.selected_customer_orders.filter((order) => !this.is_order_paid(order));
        },
        selected_customer_paid_orders() {
            return this.selected_customer_orders.filter((order) => this.is_order_paid(order));
        },
        selected_customer_unpaid_total() {
            return this.selected_customer_unpaid_orders.reduce((sum, order) => {
                return sum + Number(order?.total_amount || 0);
            }, 0);
        },
        active_month_label() {
            return this.credit_selected_month === "all"
                ? "All Months"
                : format_month_label(this.credit_selected_month);
        },
        sales_active_month_label() {
            return this.sales_selected_month === "all"
                ? "All Months"
                : format_month_label(this.sales_selected_month);
        },
    },
    watch: {
        active_dashboard_tab() {
            if (this.active_dashboard_tab !== "credit") {
                this.selected_customer_id = null;}
        },
        customer_accounts: {
            handler(accounts) {
                if (this.selected_customer_id) {
                    if (this.customer_page > this.customer_total_pages) {
                        this.customer_page = this.customer_total_pages;
                    }
                    return;
                }

                if (!accounts.length) {
                    this.selected_customer_id = null;
                    this.customer_page = 1;
                    return;
                }

                const still_exists = accounts.some((account) => account.id === this.selected_customer_id);
                if (!still_exists) {
                    this.selected_customer_id = null;
                }

                if (this.customer_page > this.customer_total_pages) {
                    this.customer_page = this.customer_total_pages;
                }
            },
            immediate: true,
        },
        sales_selected_month() {
            this.sales_page = 1;
        },
        credit_selected_month() {
            this.customer_page = 1;
        },
        customer_query() {
            this.customer_page = 1;
        },
        credit_status_filter() {
            this.customer_page = 1;
            this.selected_customer_id = null;
        },
        sales_query() {
            this.sales_page = 1;
        },
        sales_payment_filter() {
            this.sales_page = 1;
        },
        sales_delivery_filter() {
            this.sales_page = 1;
        },
        filtered_sales_orders: {
            handler() {
                if (this.sales_page > this.sales_total_pages) {
                    this.sales_page = this.sales_total_pages;} },
            immediate: true,
        },
    },
    methods: {
        get_orders() {
            const payload = {
                base_profile_id: this.base_profile_id,
            };
            this.loading = true;

            $.ajax({
                type: "POST",
                url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_orders/`,
                headers: {
                    Authorization: `Token ${get_token()}`,
                },
                data: JSON.stringify(payload),
                contentType: "application/json",
                success: (response) => {
                    if (response.status === "success") {
                        this.all_orders = response.data || [];
                    }
                },
                error: (xhr) => {
                    if (xhr.responseJSON?.errors) {
                        this.errors = xhr.responseJSON.errors;
                    }
                },
                complete: () => {
                    this.loading = false;
                }
            });
        },
        is_credit_order,
        get_order_status,
        format_status_label,
        is_order_paid,
        get_order_payment_label,
        get_order_delivery_label,
        get_order_month_key,
        format_month_label,
        format_price,
        format_short_date,
        get_order_products_label,
        is_sales_order_expanded(order) {
            return this.expanded_sales_order_ids.includes(order?.id);
        },
        toggle_sales_order(order) {
            if (!order?.id) return;

            if (this.is_sales_order_expanded(order)) {
                this.expanded_sales_order_ids = this.expanded_sales_order_ids.filter((id) => id !== order.id);
                return;
            }

            this.expanded_sales_order_ids = [...this.expanded_sales_order_ids, order.id];
        },
        is_customer_account_expanded(customer) {
            return this.expanded_customer_account_ids.includes(customer?.id);
        },
        toggle_customer_account(customer) {
            if (!customer?.id) return;

            if (this.is_customer_account_expanded(customer)) {
                this.expanded_customer_account_ids = this.expanded_customer_account_ids.filter((id) => id !== customer.id);
                return;
            }

            this.expanded_customer_account_ids = [...this.expanded_customer_account_ids, customer.id];
        },
        get_customer_initials,
        select_customer(customer) {
            this.selected_customer_id = customer?.id || null;
        },
        clear_selected_customer() {
            this.selected_customer_id = null;
        },
        previous_customer_page() {
            if (this.customer_page <= 1) return;
            this.customer_page -= 1;
        },
        next_customer_page() {
            if (this.customer_page >= this.customer_total_pages) return;
            this.customer_page += 1;
        },
        previous_sales_page() {
            if (this.sales_page <= 1) return;
            this.sales_page -= 1;
        },
        next_sales_page() {
            if (this.sales_page >= this.sales_total_pages) return;
            this.sales_page += 1;
        },
        mark_order_paid(order) {
            if (!order?.id) return;

            const payload = {
                base_profile_id: this.base_profile_id,
                order_id: order.id,
                status: "paid",
            };
            order.is_loading = true;

            $.ajax({
                type: "POST",
                url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/update_order_status/`,
                headers: {
                    Authorization: `Token ${get_token()}`,
                },
                data: JSON.stringify(payload),
                contentType: "application/json",
                success: (response) => {
                    if (response.status === "success") {
                        this.get_orders();
                    }
                },
                error: (xhr) => {
                    if (xhr.responseJSON?.errors) {
                        this.errors = xhr.responseJSON.errors;
                    }
                },
                complete: () => {
                    order.is_loading = false;
                }
            });
        },
        mark_all_customer_orders_paid() {
            if (!this.selected_customer_unpaid_orders.length) return;

            this.is_loading = true;
            let remaining_orders = this.selected_customer_unpaid_orders.length;

            this.selected_customer_unpaid_orders.forEach((order) => {
                if (!order?.id) {
                    remaining_orders -= 1;
                    if (remaining_orders <= 0) {
                        this.is_loading = false;
                    }
                    return;
                }

                const payload = {
                    base_profile_id: this.base_profile_id,
                    order_id: order.id,
                    status: "paid",
                };

                $.ajax({
                    type: "POST",
                    url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/update_order_status/`,
                    headers: {
                        Authorization: `Token ${get_token()}`,
                    },
                    data: JSON.stringify(payload),
                    contentType: "application/json",
                    success: (response) => {
                        if (response.status === "success") {
                            this.get_orders();
                        }
                    },
                    error: (xhr) => {
                        if (xhr.responseJSON?.errors) {
                            this.errors = xhr.responseJSON.errors;
                        }
                    },
                    complete: () => {
                        remaining_orders -= 1;
                        if (remaining_orders <= 0) {
                            this.is_loading = false;
                        }
                    }
                });
            });
        },
        customer_has_unpaid_orders(customer) {
            return Number(customer?.outstanding_amount || 0) > 0;
        },
        go_back() {
            this.$router.push({ name: "orders", query: { base_profile_id: this.base_profile_id } });
        },
    },
    mounted() {
        this.get_orders();
    },
};
